import { Trans, useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps } from 'react-native'
import { InsightWorkspace, useFloor, useFloors, useInsightsFloor } from 'zens-store'
import {
  CalendarValue,
  Color,
  Hyperlink,
  Spacing,
  Text,
  Radius,
  calendarValueToInsightsFromTo,
  Weekday,
} from 'zens-storybook'
import { isNumber } from 'lodash'
import { Link } from 'zens-router'
import { heatmapColor } from '@dms/helper'

import { InfoBlock } from '../info'
import { FloorplanView, FloorplanZoomView } from '../floorplan'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
  },
  info: {
    height: '100%',
  },
  more: {
    marginTop: Spacing.M,
    alignSelf: 'flex-end',
  },
  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    color: Color.metalGrey,
  },
  skeletonMap: {
    flex: 1,
    minHeight: 200,
    backgroundColor: Color.sleepingGrey,
    borderRadius: Radius.S,
    marginBottom: 46,
  },
})

interface Props extends ViewProps {
  companyId: number
  buildingId: number
  floorId?: number
  period: CalendarValue
  weekdays?: Weekday[]
  workingHours?: boolean
}

const InsightsHeatmapSummary = (props: Props) => {
  const { companyId, buildingId, floorId, period, weekdays, workingHours, style, ...otherProps } = props
  const { t } = useTranslation()
  const floors = useFloors({ companyId, buildingId })
  const floor = useFloor({ companyId, floorId: floorId || floors.data?.[0]?.id || 0 })
  const dates = calendarValueToInsightsFromTo(period, weekdays)
  const insights = useInsightsFloor(
    {
      companyId,
      floorId: floorId || floors.data?.[0]?.id || 0,
      from: dates?.from || new Date(),
      to: dates?.to || new Date(),
      weekdays,
      workingHours,
    },
    { enabled: !!dates },
  )
  const Title = (
    <>
      <Text level={4}>{t('Historical heatmap')}</Text>
      <Text>{floor.data?.displayName || floors.data?.[0]?.displayName || ' '}</Text>
    </>
  )

  function colorForInsight(insight: InsightWorkspace) {
    return isNumber(insight.value) ? heatmapColor(insight.value) : Color.calmGrey
  }

  function renderEmpty() {
    return (
      <View style={styles.empty}>
        <Text style={styles.emptyText}>
          <Trans>There is nothing here yet. Navigate to Company tab to add a floorplan.</Trans>
        </Text>
      </View>
    )
  }
  return (
    <InfoBlock title={Title} style={[styles.container, style]} contentStyle={styles.info} {...otherProps}>
      {floor.data?.floorplanBackground ? (
        <FloorplanZoomView disabled testID="InsightsHeatmapSummary.Floorplan">
          <FloorplanView
            companyId={companyId}
            floorId={floorId || floors.data?.[0]?.id || 0}
            data={insights.data}
            itemColor={(item) => colorForInsight(item as InsightWorkspace)}
          />
        </FloorplanZoomView>
      ) : (
        renderEmpty()
      )}
      <Link href={`/companies/${companyId}/insights/${buildingId}/floors/${floorId}`} asChild>
        <Hyperlink
          title={t('View more')}
          right="right"
          iconSize={14}
          style={styles.more}
          testID="InsightsHeatmapSummary.More"
        />
      </Link>
    </InfoBlock>
  )
}

interface SkeletonProps extends ViewProps {}

InsightsHeatmapSummary.Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  return (
    <InfoBlock.Skeleton
      style={[styles.container, style]}
      title={
        <>
          <Text.Skeleton width={200} level={4} />
          <Text.Skeleton width={160} />
        </>
      }
      {...otherProps}
    >
      <View style={styles.skeletonMap} />
    </InfoBlock.Skeleton>
  )
}

export default InsightsHeatmapSummary
